import { TFunction } from 'i18next';
import { DateTimePicker } from '../DateTimePicker';
import { Options, Select } from '@/components/Controls/Select';
import { NotFound } from '@/components/Message/NotFound';
import { Checkbox } from '@/components/Controls/Checkbox';
import dayjs, { Dayjs } from 'dayjs';

type VehicleFilterProps = {
  t: TFunction<undefined>;
  isLoadingAllGroups?: boolean;
  isLoadingAllCompanies?: boolean;
  groupOptions?: Options[];
  companyOptions?: Options[];
  displayGroupFilter?: boolean;
  displayCompanyFilter?: boolean;
  startDate?: string;
  endDate?: string;
  requestInspection?: string;
  groupId?: string;
  companyId?: string;
  searchBeginDate?: (value: Dayjs) => void;
  searchEndDate?: (value: Dayjs) => void;
  searchByRequestInspect?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  searchByGroup?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  searchByCompany?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
};

export const VehicleFilter = ({
  t,
  isLoadingAllGroups,
  isLoadingAllCompanies,
  groupOptions,
  companyOptions,
  displayGroupFilter,
  displayCompanyFilter,
  startDate,
  endDate,
  requestInspection,
  groupId,
  companyId,
  searchBeginDate,
  searchEndDate,
  searchByRequestInspect,
  searchByGroup,
  searchByCompany,
}: VehicleFilterProps) => {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-row gap-3 select-datepicker-container">
        <div className="flex flex-row gap-3">
          <label className="text-gray-600 text-sm self-center">
            {t('common:createdBetween')}
          </label>
          <DateTimePicker
            useTime={false}
            value={startDate ? dayjs(startDate) : null}
            onChange={searchBeginDate}
            disablePast={false}
            numberOfAllowedFutureDays={0}
            maxDate={endDate ? dayjs(endDate) : null}
          />
        </div>
        <div className="flex flex-row gap-3">
          <label className="text-gray-600 text-sm self-center">
            {t('common:and').toLowerCase()}
          </label>
          <DateTimePicker
            useTime={false}
            value={endDate ? dayjs(endDate) : null}
            onChange={searchEndDate}
            disablePast={false}
            numberOfAllowedFutureDays={1}
            minDate={startDate ? dayjs(startDate) : null}
          />
        </div>
      </div>
      <div>
        <Checkbox
          checkboxClassName="-mt-0.5 !w-[17.5px] !h-[17.5px]"
          textClassName="text-sm text-gray-600"
          text={t('vehicleInspectionRequest')}
          checked={requestInspection === 'true'}
          onChange={searchByRequestInspect}
        />
      </div>
      <div className="flex flex-col gap-3">
        {displayGroupFilter && (
          <div className="flex items-center gap-3">
            <div className="flex flex-1 text-gray-600 text-sm">
              {t('common:linkedToGroup')}
            </div>
            <div className="flex flex-1">
              {isLoadingAllGroups ? (
                <Select
                  selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500"
                  placeholder={t('common:allGroups')}
                  options={[]}
                />
              ) : (
                <Select
                  selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500"
                  placeholder={t('common:allGroups')}
                  options={groupOptions}
                  value={groupId || ''}
                  onChange={searchByGroup}
                />
              )}
            </div>
          </div>
        )}
        {displayCompanyFilter && (
          <div className="flex items-center gap-3">
            <div className="flex flex-1 text-gray-600 text-sm">
              {t('common:linkedToCompany')}
            </div>
            <div className="flex flex-1">
              {isLoadingAllCompanies ? (
                <Select
                  selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500"
                  placeholder={t('common:allCompanies')}
                  options={[]}
                />
              ) : groupId && companyOptions?.length === 0 ? (
                <NotFound
                  containerClassName="max-w-60 gap-1.5"
                  iconClassName="-mt-1.5 text-lg"
                  textClassName="text-2sm"
                  text={t('common:noCompanyLinkedToThisGroup')}
                />
              ) : companyOptions?.length === 0 ? (
                <NotFound
                  containerClassName="max-w-60 gap-1.5"
                  iconClassName="-mt-1.5 text-lg"
                  textClassName="text-2sm"
                  text={t('common:youAreNotLinkedToAnyCompany')}
                />
              ) : (
                <Select
                  selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500"
                  placeholder={t('common:allCompanies')}
                  options={companyOptions}
                  value={companyId || ''}
                  onChange={searchByCompany}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
